import React, { useEffect, useRef, useState } from "react";

/**
 * EliteKanbanBoard.jsx — React 17.0.2
 * - 6 lists: Planning, Scheduled, In Production, QC, Paused/Hold, Completed
 * - Planning built from GET /production/orders (only items with decorations)
 * - Department inference from chargeName/chargeId
 * - Adjacent-only movement across main columns
 * - Paused/Hold can accept from any; may return only to previous main column
 * - Move Gate when moving to Scheduled (Operator, Machine, Planned Start/Finish are required)
 * - Department claim tag shown once Scheduled; cleared if returned to Planning
 * - Job Card pop-out with requested sections + lifecycle + Process/BOM buttons
 */

const STATUSES = [
  { key: "Planned",        label: "Upcoming / Planning" },
  { key: "Scheduled",      label: "Scheduled" },
  { key: "In_Production",  label: "In Production" },
  { key: "QC",             label: "QC" },
  { key: "Paused",         label: "Paused / Hold" },
  { key: "Completed",      label: "Completed" },
];
const STATUS_INDEX = STATUSES.reduce((m, s, i) => (m[s.key] = i, m), {});

// Department inference (chargeName → department; optional chargeId overrides)
const DEPT_MATCHERS = {
  Screening:  [/screen ?print/i, /pad ?print/i, /silk/i],
  Embroidery: [/embroider/i],
  "Direct to Film": [/\bDTF\b/i, /direct\s*to\s*film/i],
  Engraving:  [/engrave/i, /laser/i],
  Cresting:   [/crest/i, /heat ?press/i, /vinyl/i],
  Awards:     [/award/i, /trophy/i],
};
const CHARGE_ID_TO_DEPT = { 15: "Embroidery", 45: "Screening", 107: "Direct to Film" };


function inferDepartmentForDeco(deco) {
  if (!deco) return "";
  if (deco.chargeId != null && CHARGE_ID_TO_DEPT[deco.chargeId]) return CHARGE_ID_TO_DEPT[deco.chargeId];
  const name = String(deco.chargeName || "");
  for (const [dept, tests] of Object.entries(DEPT_MATCHERS)) {
    if (tests.some(re => re.test(name))) return dept;
  }
  return "";
}

// ────────────────────────────────────────────────────────────────────────────
// Job Card modal (client-requested fields)
// ────────────────────────────────────────────────────────────────────────────
function JobCardModal({ open, onClose, data }) {
  if (!open || !data) return null;
  const money = (n) => (typeof n === "number" ? n.toFixed(2) : "–");
  const section = { border: "1px solid #eee", borderRadius: 12, padding: 12, marginTop: 10 };
  const label = { fontSize: 12, color: "#666" };
  const value = { fontSize: 14, fontWeight: 500 };
  const chips = { display: "flex", gap: 8, flexWrap: "wrap" };
  const Row = ({ l, v }) => (<div><div style={label}>{l}</div><div style={value}>{(v ?? v===0) && v!=="" ? v : "–"}</div></div>);

  const p = data.project || {};
  const pr = data.product || {};
  const d  = data.decoration || {};
  const a  = data.art || {};
  const ctx = data.context || {};
  const lf  = data.lifecycle || [];

  return (
    <div style={modalStyles.overlay} onClick={onClose}>
      <div style={modalStyles.card} onClick={(e) => e.stopPropagation()}>
        <div style={modalStyles.header}>
          <h3 style={{ margin: 0 }}>Job Card</h3>
          <button onClick={onClose}>Close</button>
        </div>

        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", fontSize: 13 }}>
          <span><b>Order:</b> {p.projectNumber || "–"}</span>
          <span>•</span>
          <span><b>Customer:</b> {ctx.customer || "–"}</span>
          <span>•</span>
          <span><b>In-hands:</b> {ctx.inHandsDate ? String(ctx.inHandsDate).slice(0,10) : "–"}</span>
          <span>•</span>
          <span><b>Qty:</b> {ctx.quantity ?? "–"}</span>
        </div>

        <div style={section}>
          <div style={{ ...label, marginBottom: 6, fontWeight: 600 }}>Project Information</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 10 }}>
            <Row l="Project name" v={p.projectName} />
            <Row l="Project number" v={p.projectNumber} />
            <div>
              <div style={label}>PO Numbers</div>
              <div style={chips}>
                {(p.poNumbers||[]).length
                  ? p.poNumbers.map((x,i)=>(<span key={i} style={{ border:"1px solid #ddd", borderRadius:999, padding:"2px 8px", fontSize:12 }}>{x}</span>))
                  : <span style={value}>–</span>}
              </div>
            </div>
          </div>
        </div>

        <div style={section}>
          <div style={{ ...label, marginBottom: 6, fontWeight: 600 }}>Product information</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 10 }}>
            <Row l="SKU" v={pr.sku} />
            <Row l="Sizes" v={(pr.sizes||[]).join(", ")} />
            <Row l="Colors" v={(pr.colors||[]).join(", ")} />
            <Row l="Net cost goods" v={money(pr.netCostGoods)} />
            <Row l="Retail cost goods" v={money(pr.retailCostGoods)} />
            <Row l="Supplier information" v={pr.supplier} />
            <Row l="Category" v={pr.category} />
          </div>
        </div>

        <div style={section}>
          <div style={{ ...label, marginBottom: 6, fontWeight: 600 }}>Decoration information</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 10 }}>
            <Row l="Deco department" v={d.department} />
            <Row l="Matrix pricing" v={d.matrixPricing} />
            <Row l="Net cost goods" v={money(d.netCostGoods)} />
            <Row l="Retail cost goods" v={money(d.retailCostGoods)} />
            <Row l="Location" v={d.location} />
          </div>
        </div>

        <div style={section}>
          <div style={{ ...label, marginBottom: 6, fontWeight: 600 }}>Art information</div>
          {a.artProofUrl ? (
            <a href={a.artProofUrl} target="_blank" rel="noreferrer" style={{ textDecoration: "underline" }}>Open proof</a>
          ) : <div style={value}>–</div>}
        </div>

        <div style={section}>
          <div style={{ ...label, marginBottom: 6, fontWeight: 600 }}>Lifecycle</div>
          <ol style={{ margin: 0, paddingLeft: 18, fontSize: 12 }}>
            {lf.length ? lf.map((ev, i) => (
              <li key={i}><b>{ev.when || ""}</b>: {ev.text || ""}</li>
            )) : <li>–</li>}
          </ol>
        </div>

        <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
          <button style={{ border: "1px solid #ddd", borderRadius: 8, padding: "6px 10px", background: "#fff" }}>Add Process</button>
          <button style={{ border: "1px solid #ddd", borderRadius: 8, padding: "6px 10px", background: "#fff" }}>Add BOM</button>
        </div>
      </div>
    </div>
  );
}

const modalStyles = {
  overlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,.4)", display: "flex", justifyContent: "center", alignItems: "center", zIndex: 9999 },
  card: { background: "#fff", borderRadius: 12, padding: 20, width: "min(860px, 95vw)", maxHeight: "90vh", overflow: "auto" },
  header: { display: "flex", justifyContent: "space-between", marginBottom: 10 },
};

// ────────────────────────────────────────────────────────────────────────────
// Move Gate modal (required when moving to Scheduled)
// ────────────────────────────────────────────────────────────────────────────
function MoveGateModal({ to, fields, onCancel, onConfirm }) {
  const [form, setForm] = useState(fields || { operator: "", machine: "", plannedStart: "", plannedFinish: "", reason: "", isFirm: false });
  const req = to === "Scheduled";
  const disabled = req && (!form.operator || !form.machine || !form.plannedStart || !form.plannedFinish);

  return (
    <div style={modalStyles.overlay}>
      <div style={{ ...modalStyles.card, width: "min(720px, 95vw)" }}>
        <div style={modalStyles.header}>
          <h3 style={{ margin: 0 }}>Move to {to}</h3>
          <button onClick={onCancel}>Cancel</button>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <label><div>Operator *</div><input value={form.operator} onChange={e=>setForm({ ...form, operator: e.target.value })} /></label>
          <label><div>Machine *</div><input value={form.machine} onChange={e=>setForm({ ...form, machine: e.target.value })} /></label>
          <label><div>Planned Start *</div><input type="datetime-local" value={form.plannedStart} onChange={e=>setForm({ ...form, plannedStart: e.target.value })} /></label>
          <label><div>Planned Finish *</div><input type="datetime-local" value={form.plannedFinish} onChange={e=>setForm({ ...form, plannedFinish: e.target.value })} /></label>
          {to === "Scheduled" && (
            <label style={{ gridColumn: "1 / -1", display: "flex", alignItems: "flex-start", gap: 8 }}>
              <input
                type="checkbox"
                checked={!!form.isFirm}
                onChange={e => setForm({ ...form, isFirm: e.target.checked })}
                style={{ marginTop: 2 }}
              />
              <div>
                <div>Firm date</div>
                <div style={{ fontSize: 12, color: "#6b7280" }}>This job must be fulfilled on time</div>
              </div>
            </label>
          )}
          <label style={{ gridColumn: "1 / -1" }}><div>Reason (if changing/removing)</div><input value={form.reason} onChange={e=>setForm({ ...form, reason: e.target.value })} /></label>
        </div>

        <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
          <button disabled={disabled} onClick={()=>onConfirm(form)} style={{ opacity: disabled ? .6 : 1 }}>Confirm Move</button>
        </div>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
export default function EliteKanbanBoard() {
  const [columns, setColumns]   = useState(() => emptyColumns());
  const [error, setError]       = useState("");
  const [loading, setLoading]   = useState(false);
  const [planningDept]          = useState("Screening"); // future: make selectable
  const [modalOpen, setModalOpen] = useState(false);
  const [modalData, setModalData] = useState(null);
  const [moveModal, setMoveModal] = useState({ open: false, to: null, card: null, fields: { operator:"", machine:"", plannedStart:"", plannedFinish:"", reason:"", isFirm: false } });
  const dragRef = useRef({ cardId: null, from: null, lastMain: null });

  function emptyColumns() {
    const obj = {}; STATUSES.forEach(s => obj[s.key] = []); return obj;
  }

  // Load Planning from /production/orders (only items with decorations)
  useEffect(() => {
    (async () => {
      setLoading(true); setError("");
      try {
        const res = await fetch(`http://localhost:3001/production/orders?page=1&pageSize=50`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        const planning = buildPlanningCards(json?.data || []);
        const next = emptyColumns();
        next.Planned = planning;
        setColumns(next);
      } catch (e) {
        setError(e.message || String(e));
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function buildPlanningCards(orders) {
    const out = [];
    for (const o of orders) {
      for (const it of (o.items || [])) {
        const decos = (it.decorations || []).filter(Boolean);
        if (!decos.length) continue; // only items with ≥1 decoration
        for (const d0 of decos) {
          const dept = inferDepartmentForDeco(d0);
          out.push({
            id: `ORD-${o.orderNumber}-${it.itemId}-${d0.decorationId}`,
            status: "Planned",
            orderNumber: o.orderNumber,
            customer: o.customerName || "",
            productName: it.productName || "",
            sku: it.productId || "",
            matrix: d0.chargeName || "",
            location: d0.location || "",
            qtyPlanned: Number(it.quantity || 0) || 0,
            qtyDone: 0,
            dueDate: o.inHandsDate || "",
            department: dept,          // for display
            claimedDept: "",           // becomes non-empty when scheduled
            lifecycle: [],
            // Raw to feed the Job Card modal
            raw: {
              order: {
                orderNumber: o.orderNumber,
                customerName: o.customerName,
                inHandsDate: o.inHandsDate,
                projectName: o.projectName || "",
                poNumbers: Array.isArray(o.poNumbers) ? o.poNumbers : [],
              },
              item: {
                itemId: it.itemId,
                productId: it.productId,
                productName: it.productName,
                quantity: it.quantity,
                sizes: Array.isArray(it.sizes) ? it.sizes : [],
                colors: Array.isArray(it.colors) ? it.colors : [],
                netCostGoods: it.netCostGoods ?? null,
                retailCostGoods: it.retailCostGoods ?? null,
                supplierName: it.supplierName || "",
                category: it.category || "",
              },
              deco: {
                decorationId: d0.decorationId,
                chargeId: d0.chargeId,
                chargeName: d0.chargeName,
                location: d0.location,
                netCostGoods: d0.netCostGoods ?? null,
                retailCostGoods: d0.retailCostGoods ?? null,
                artProofUrl: d0.artProofUrl || "",
              },
            },
          });
        }
      }
    }
    return out;
  }

  function canDrop(fromKey, toKey) {
    if (toKey === "Paused") return true;   // Paused can accept from anywhere
    if (fromKey === "Paused") return true; // Return is handled below
    const fi = STATUS_INDEX[fromKey], ti = STATUS_INDEX[toKey];
    return Math.abs(ti - fi) === 1;
  }

  function onDragStart(e, card, fromCol) {
    const isMain = fromCol !== "Paused";
    dragRef.current = { cardId: card.id, from: fromCol, lastMain: isMain ? fromCol : (card._lastMain || "Planned") };
    e.dataTransfer.effectAllowed = "move";
  }

  function onDragOver(e) { e.preventDefault(); }

  function onDrop(e, toCol) {
    e.preventDefault();
    const { cardId, from, lastMain } = dragRef.current || {};
    if (!cardId || !from) return;

    // Enforce adjacency across main columns
    if (toCol !== "Paused" && from !== "Paused" && !canDrop(from, toCol)) {
      alert("Only adjacent moves allowed (except Paused/Hold).");
      return;
    }

    const next = JSON.parse(JSON.stringify(columns));
    const idx = next[from].findIndex(c => c.id === cardId);
    if (idx === -1) return;
    const [moved] = next[from].splice(idx, 1);

    // Move Gate to Scheduled: require operator/machine/planned times
    if (toCol === "Scheduled") {
      setMoveModal({
        open: true,
        to: toCol,
        card: moved,
        fields: { operator: "", machine: "", plannedStart: "", plannedFinish: "", reason: "", isFirm: false }
      });
      // Undo removal until user confirms
      next[from].splice(idx, 0, moved);
      setColumns(next);
      return;
    }

    // Paused rules
    if (toCol === "Paused") {
      moved._lastMain = from === "Paused" ? lastMain : from;
      moved.lifecycle = moved.lifecycle || [];
      moved.lifecycle.push({ when: new Date().toLocaleString(), text: `Moved to Paused/Hold from ${from}` });
    }
    if (from === "Paused" && toCol !== (moved._lastMain || "Planned")) {
      alert(`Paused card can only return to ${moved._lastMain}.`);
      // Put back
      next[from].splice(idx, 0, moved);
      setColumns(next);
      return;
    }

    // If returning to Planning, clear claim
    if (toCol === "Planned") {
      moved.claimedDept = "";
      moved.lifecycle = moved.lifecycle || [];
      moved.lifecycle.push({ when: new Date().toLocaleString(), text: "Returned to Planning (open to all)" });
    }

    moved.status = toCol;
    next[toCol].unshift(moved);
    setColumns(next);
  }

  function openJobCard(card) {
    const o = card.raw?.order || {};
    const it = card.raw?.item || {};
    const d = card.raw?.deco || {};
    const dept = card.claimedDept || card.department || inferDepartmentForDeco(d);

    const job = {
      project: {
        projectName: o.projectName || "",
        projectNumber: o.orderNumber || "",
        poNumbers: Array.isArray(o.poNumbers) ? o.poNumbers : [],
      },
      product: {
        sku: it.productId || "",
        sizes: it.sizes || [],
        colors: it.colors || [],
        netCostGoods: it.netCostGoods ?? null,
        retailCostGoods: it.retailCostGoods ?? null,
        supplier: it.supplierName || "",
        category: it.category || "",
      },
      decoration: {
        department: dept || "",
        matrixPricing: d.chargeName || card.matrix || "",
        netCostGoods: d.netCostGoods ?? null,
        retailCostGoods: d.retailCostGoods ?? null,
        location: d.location || card.location || "",
      },
      art: { artProofUrl: d.artProofUrl || "" },
      context: {
        customer: o.customerName || card.customer || "",
        inHandsDate: o.inHandsDate || card.dueDate || "",
        quantity: it.quantity ?? card.qtyPlanned ?? 0,
      },
      lifecycle: card.lifecycle || [],
    };
    setModalData(job);
    setModalOpen(true);
  }

  return (
    <div style={styles.wrap}>
      <header style={styles.toolbar}>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <strong>Planning</strong>
          {loading && <span style={styles.pill}>Loading…</span>}
          {error && <span style={{ ...styles.pill, background: "#fee2e2", borderColor: "#fecaca", color: "#991b1b" }}>Error: {error}</span>}
        </div>
      </header>
      <div style={styles.board}>
        {STATUSES.map(({ key, label }) => (
          <section key={key} style={styles.column} onDragOver={onDragOver} onDrop={(e) => onDrop(e, key)}>
            <div style={styles.colHeader}><h3 style={{ margin: 0 }}>{label}</h3></div>
            <div style={styles.cardList}>
              {(columns[key] || []).map((c) => (
                <article key={c.id} style={styles.card} draggable onDragStart={(e) => onDragStart(e, c, key)}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
                    <div>
                      <div style={{ fontWeight: 600 }}>{c.orderNumber || c.title || "—"}</div>
                      <div style={{ fontSize: 12, color: "#555" }}>{c.customer || ""}</div>
                      <div style={{ fontSize: 12, color: "#667" }}>
                        {c.productName || ""} <span style={{ color: "#999" }}>({c.sku || ""})</span>
                      </div>
                    </div>
                    <span style={styles.badge}>
                      {c.claimedDept ? `${c.claimedDept} • Planned` : (c.department || key.replace(/_/g,' '))}
                    </span>
                  </div>

                  <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 8, marginTop: 6, fontSize: 12 }}>
                    <div><div style={{ color: "#667" }}>Qty</div><div style={{ fontWeight: 600 }}>{c.qtyPlanned ?? 0}</div></div>
                    <div><div style={{ color: "#667" }}>Location</div><div style={{ fontWeight: 600 }}>{c.location || "–"}</div></div>
                    <div><div style={{ color: "#667" }}>Matrix</div><div style={{ fontWeight: 600 }}>{c.matrix || "–"}</div></div>
                  </div>

                  <div style={{ marginTop: 8, display: "flex", gap: 6 }}>
                    <button style={styles.linkBtn} onClick={() => openJobCard(c)}>Open</button>
                  </div>
                </article>
              ))}
            </div>
          </section>
        ))}
      </div>
      <JobCardModal open={modalOpen} onClose={() => setModalOpen(false)} data={modalData} />
      {moveModal.open && (
        <MoveGateModal
          to={moveModal.to}
          fields={moveModal.fields}
          onCancel={() => setMoveModal({ ...moveModal, open: false })}
          onConfirm={(fields) => {
            // On confirm: claim dept, set planned times, operator/machine, move to Scheduled
            const next = JSON.parse(JSON.stringify(columns));
            const fromArrs = Object.keys(next).map(k => [k, next[k]]);
            let fromKey = null, idx = -1;
            for (const [k, arr] of fromArrs) {
              const i = arr.findIndex(x => x.id === moveModal.card.id);
              if (i !== -1) { fromKey = k; idx = i; break; }
            }
            if (fromKey == null) return setMoveModal({ ...moveModal, open: false });

            const [moved] = next[fromKey].splice(idx, 1);
            const dept = moved.department || inferDepartmentForDeco(moved.raw?.deco) || "Department";
            moved.claimedDept = dept;
            moved.plannedStart = fields.plannedStart;
            moved.plannedFinish = fields.plannedFinish;
            moved.operator = fields.operator;
            moved.machine = fields.machine;
            moved.isFirm = !!fields.isFirm;
            moved.lifecycle = moved.lifecycle || [];
            moved.lifecycle.push({
              when: new Date().toLocaleString(),
              text: `Scheduled: ${fields.operator || "Unassigned"} on ${fields.machine || "TBD"}`
            });
            moved.status = "Scheduled";
            (next.Scheduled = next.Scheduled || []).unshift(moved);
            setColumns(next);
            setMoveModal({ ...moveModal, open: false });

            // TODO: Persist (uncomment once endpoint exists)
            // fetch(`/production/steps/${encodeURIComponent(moved.id)}/status`, {
            //   method: "PATCH",
            //   headers: { "Content-Type": "application/json" },
            //   body: JSON.stringify({ toStatus: "Scheduled", operator: fields.operator, machine: fields.machine, plannedStart: fields.plannedStart, plannedFinish: fields.plannedFinish, isFirm: !!fields.isFirm })
            // });
          }}
        />
      )}
    </div>
  );
}

const styles = {
  wrap: { padding: 16, overflowX: "auto" },
  toolbar: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 },
  board: { display: "flex", gap: 16, minWidth: "1800px" },
  column: { flex: "0 0 320px", background: "#f8f9fb", border: "1px solid #eef0f5", borderRadius: 12, display: "flex", flexDirection: "column", maxHeight: "calc(100vh - 160px)" },
  colHeader: { padding: 12, borderBottom: "1px solid #eef0f5", background: "#f1f5f9", borderTopLeftRadius: 12, borderTopRightRadius: 12 },
  cardList: { padding: 10, overflowY: "auto", flex: 1 },
  card: { background: "#fff", border: "1px solid #ddd", borderRadius: 10, padding: 10, marginBottom: 10, boxShadow: "0 1px 3px rgba(0,0,0,0.05)" },
  linkBtn: { marginTop: 6, border: "1px solid #ccc", borderRadius: 6, padding: "4px 8px", fontSize: 12, cursor: "pointer", background: "#fff" },
  pill: { border: "1px solid #dbeafe", background: "#eff6ff", color: "#1d4ed8", borderRadius: 999, padding: "2px 8px", fontSize: 12 },
  badge: { background: "#eef7ff", color: "#175cd3", border: "1px solid #d6e8ff", borderRadius: 999, padding: "2px 8px", fontSize: 12, whiteSpace: "nowrap" },
};
